import React from "react";
import { Typography } from "antd";
import cn from "../../utils/cn";

const { Title, Text, Paragraph, Link } = Typography;

// ============================================================================
// PRESETS — named roles for the app's recurring text styles
// ============================================================================
// `variant` says *what element* to render (text / title / paragraph / link).
// `preset` says *what this text is for* (a card title, a stat label, a table
// cell), and resolves to the element plus the AntD props that style it.
//
// Styles were being spelled out inline as class strings ("text-sm font-semibold
// text-black-88") in every card, drawer and table cell, so a design change meant
// a find-and-replace. A preset is defined once here and callers ask for it by
// name, so restyling is editing `PRESET_CONFIG` — no call site changes.
//
// Presets lean on AntD's own API wherever it has one — `type` for colour,
// `strong` for weight, `level` for heading size — so text keeps following the
// ConfigProvider theme. Classes appear only where AntD has no equivalent (there
// is no font-size prop on Text, so small print needs `text-xs`).
//
// Anything explicit still wins: `<CustomTypography preset={TYPOGRAPHY.CAPTION}
// type="danger" className="text-sm">` overrides the preset's colour and size.

// AntD's tertiary text step (45% in light) — one shade lighter than "secondary",
// used for labels, captions and empty values. Theme-aware (src/theme).
const TERTIARY_COLOR = "text-tertiary";
// Headings drop AntD's default bottom margin; inline headers space themselves.
const NO_MARGIN = "!mb-0";

// Preset names describe the role, not the look ("CARD_TITLE", not
// "SMALL_BOLD"), so a restyle doesn't make the name a lie.
export const TYPOGRAPHY = {
  // Headings
  PAGE_TITLE: "pageTitle",
  SECTION_TITLE: "sectionTitle",
  // A heading inside a section — "Contact details", a nested settings group.
  SUBSECTION_TITLE: "subsectionTitle",
  DRAWER_TITLE: "drawerTitle",
  DRAWER_SUBTITLE: "drawerSubtitle",
  CARD_TITLE: "cardTitle",

  // Body
  BODY: "body",
  BODY_STRONG: "bodyStrong",
  BODY_MUTED: "bodyMuted",
  BODY_SUBTLE: "bodySubtle",

  // Small print
  CAPTION: "caption",
  CAPTION_STRONG: "captionStrong",
  OVERLINE: "overline",

  // Data display
  STAT_LABEL: "statLabel",
  STAT_VALUE: "statValue",
  METRIC_VALUE: "metricValue",
  TABLE_CELL: "tableCell",
  TABLE_CELL_MUTED: "tableCellMuted",

  // Form
  FIELD_LABEL: "fieldLabel",
  CONTROL_LABEL: "controlLabel",

  // Status
  SUCCESS: "success",
  WARNING: "warning",
  ERROR: "error",
};

const PRESET_CONFIG = {
  // Headings — sized by AntD Title levels.
  [TYPOGRAPHY.PAGE_TITLE]: { variant: "title", level: 3, className: NO_MARGIN },
  [TYPOGRAPHY.SECTION_TITLE]: { variant: "title", level: 4, className: NO_MARGIN },
  [TYPOGRAPHY.SUBSECTION_TITLE]: { variant: "title", level: 5, className: NO_MARGIN },
  [TYPOGRAPHY.DRAWER_TITLE]: { variant: "title", level: 5, className: NO_MARGIN },
  [TYPOGRAPHY.DRAWER_SUBTITLE]: {
    variant: "text",
    className: cn("text-xs font-normal", TERTIARY_COLOR),
  },
  [TYPOGRAPHY.CARD_TITLE]: { variant: "text", strong: true },

  [TYPOGRAPHY.BODY]: { variant: "text" },
  [TYPOGRAPHY.BODY_STRONG]: { variant: "text", strong: true },
  [TYPOGRAPHY.BODY_MUTED]: { variant: "text", type: "secondary" },
  // AntD's `type` stops at "secondary" (65%); the design also uses the lighter
  // 45% step, hence the utility class.
  [TYPOGRAPHY.BODY_SUBTLE]: { variant: "text", className: TERTIARY_COLOR },

  [TYPOGRAPHY.CAPTION]: { variant: "text", className: cn("text-xs", TERTIARY_COLOR) },
  [TYPOGRAPHY.CAPTION_STRONG]: {
    variant: "text",
    strong: true,
    type: "secondary",
    className: "text-xs",
  },
  [TYPOGRAPHY.OVERLINE]: {
    variant: "text",
    className: cn("text-xs uppercase", TERTIARY_COLOR),
  },

  [TYPOGRAPHY.STAT_LABEL]: { variant: "text", className: cn("text-xs", TERTIARY_COLOR) },
  [TYPOGRAPHY.STAT_VALUE]: { variant: "text", strong: true },
  [TYPOGRAPHY.METRIC_VALUE]: { variant: "title", level: 4, className: NO_MARGIN },
  [TYPOGRAPHY.TABLE_CELL]: { variant: "text" },
  [TYPOGRAPHY.TABLE_CELL_MUTED]: { variant: "text", type: "secondary" },

  // App-specific label styles with no AntD counterpart.
  [TYPOGRAPHY.FIELD_LABEL]: { variant: "text", className: "form-field-label" },
  [TYPOGRAPHY.CONTROL_LABEL]: { variant: "text", type: "secondary", className: "text-xs" },

  [TYPOGRAPHY.SUCCESS]: { variant: "text", type: "success" },
  [TYPOGRAPHY.WARNING]: { variant: "text", type: "warning" },
  [TYPOGRAPHY.ERROR]: { variant: "text", type: "danger" },
};

/**
 * CustomTypography - A fully reusable Ant Design Typography component
 *
 * @example
 * // Title examples
 * <CustomTypography variant="title" level={1}>Main Heading</CustomTypography>
 * <CustomTypography variant="title" level={2} type="secondary">Subheading</CustomTypography>
 *
 * // Text examples
 * <CustomTypography variant="text">Normal text</CustomTypography>
 * <CustomTypography variant="text" type="success" strong>Success message</CustomTypography>
 *
 * // Paragraph examples
 * <CustomTypography variant="paragraph" ellipsis>Long paragraph text...</CustomTypography>
 *
 * // Link examples
 * <CustomTypography variant="link" href="https://example.com">External Link</CustomTypography>
 *
 * // Advanced features
 * <CustomTypography
 *   variant="text"
 *   copyable={{ text: "Copy this text" }}
 *   editable={{ onStart: () => console.log('edit start') }}
 * >
 *   Editable and copyable text
 * </CustomTypography>
 */

const CustomTypography = ({
  // A TYPOGRAPHY value. Supplies the element and styling props below; anything
  // passed explicitly still wins. Omit it and the component behaves exactly as
  // before.
  preset,
  variant,
  children,
  level,
  type,
  block = false,
  disabled = false,
  mark = false,
  code = false,
  keyboard = false,
  underline = false,
  delete: deleteProp = false,
  strong = false,
  italic = false,
  copyable = false,
  editable = false,
  ellipsis = false,
  href,
  target,
  className = "",
  style = {},
  onClick,
  // Title specific props
  titleProps = {},
  // Text specific props
  textProps = {},
  // Paragraph specific props
  paragraphProps = {},
  // Link specific props
  linkProps = {},
  ...rest
}) => {
  // Resolve the preset first, then let explicit props override it. The defaults
  // for `variant` / `level` moved into this fallback chain so an unset prop is
  // distinguishable from one the caller set.
  const presetConfig = (preset && PRESET_CONFIG[preset]) || {};
  const resolvedVariant = variant || presetConfig.variant || "text";
  const resolvedLevel = level ?? presetConfig.level ?? 1;

  // Common props that apply to all typography variants
  const commonProps = {
    type: type ?? presetConfig.type,
    disabled,
    // cn() resolves conflicts in the caller's favour, so className="text-sm"
    // replaces a preset's size rather than racing it on CSS order.
    className: cn(presetConfig.className, block && "block", className),
    style,
    onClick,
    ...rest,
  };

  // Text formatting props
  const formatProps = {
    mark,
    code,
    keyboard,
    underline,
    delete: deleteProp,
    strong: strong || !!presetConfig.strong,
    italic,
  };

  // Interactive props
  const interactiveProps = {
    copyable,
    editable,
  };

  switch (resolvedVariant) {
    case "title":
      return (
        <Title
          level={resolvedLevel}
          ellipsis={ellipsis}
          {...commonProps}
          {...formatProps}
          {...interactiveProps}
          {...titleProps}
        >
          {children}
        </Title>
      );

    case "text":
      return (
        <Text
          ellipsis={ellipsis}
          {...commonProps}
          {...formatProps}
          {...interactiveProps}
          {...textProps}
        >
          {children}
        </Text>
      );

    case "paragraph":
      return (
        <Paragraph
          ellipsis={ellipsis}
          {...commonProps}
          {...formatProps}
          {...interactiveProps}
          {...paragraphProps}
        >
          {children}
        </Paragraph>
      );

    case "link":
      return (
        <Link
          href={href}
          target={target}
          ellipsis={ellipsis}
          {...commonProps}
          {...formatProps}
          {...linkProps}
        >
          {children}
        </Link>
      );

    default:
      return (
        <Text
          ellipsis={ellipsis}
          {...commonProps}
          {...formatProps}
          {...interactiveProps}
          {...textProps}
        >
          {children}
        </Text>
      );
  }
};

// ============================================================================
// PREDEFINED VARIANTS - Quick Reference Guide
// ============================================================================
//
// 📌 HEADINGS (for titles and section headers)
// ----------------------------------------
// CustomTypography.Heading1 - Largest heading (H1) - Page titles
// CustomTypography.Heading2 - Second level heading (H2) - Section headers
// CustomTypography.Heading3 - Third level heading (H3) - Subsection headers
// CustomTypography.Heading4 - Fourth level heading (H4) - Small headers
// CustomTypography.Heading5 - Fifth level heading (H5) - Smallest headers
//
// 📝 TEXT SIZES (for body content)
// ----------------------------------------
// CustomTypography.BodyLarge   - Large text (16px) - Emphasis content
// CustomTypography.Body        - Normal text (14px) - ⭐ TEXT BASE/NORMAL ⭐ - Default body text
// CustomTypography.BodySmall   - Small text (12px) - Fine print, footnotes
// CustomTypography.Caption     - Caption text (12px, secondary) - Image captions, helper text
//
// 🎨 TEXT COLORS (for semantic meaning)
// ----------------------------------------
// CustomTypography.Success     - Green text - Success messages
// CustomTypography.Warning     - Orange text - Warning messages
// CustomTypography.Danger      - Red text - Error messages
// CustomTypography.Secondary   - Gray text - Less important text
//
// 🔗 SPECIAL TYPES
// ----------------------------------------
// CustomTypography.Link        - Hyperlinks
// CustomTypography.Code        - Code snippets (monospace)
// CustomTypography.Strong      - Bold text
// CustomTypography.Italic      - Italic text
// CustomTypography.Mark        - Highlighted text
//
// 💡 USAGE EXAMPLES:
// ----------------------------------------
// Text Base/Normal:     <CustomTypography.Body>Normal text here</CustomTypography.Body>
// Bold text:            <CustomTypography.Body strong>Bold text</CustomTypography.Body>
// Combined:             <CustomTypography.BodyLarge type="success" strong>Large bold success</CustomTypography.BodyLarge>
// ============================================================================

// HEADINGS
const Heading1 = props => <CustomTypography variant="title" level={1} {...props} />;
Heading1.displayName = "CustomTypography.Heading1";
CustomTypography.Heading1 = Heading1;

const Heading2 = props => <CustomTypography variant="title" level={2} {...props} />;
Heading2.displayName = "CustomTypography.Heading2";
CustomTypography.Heading2 = Heading2;

const Heading3 = props => <CustomTypography variant="title" level={3} {...props} />;
Heading3.displayName = "CustomTypography.Heading3";
CustomTypography.Heading3 = Heading3;

const Heading4 = props => <CustomTypography variant="title" level={4} {...props} />;
Heading4.displayName = "CustomTypography.Heading4";
CustomTypography.Heading4 = Heading4;

const Heading5 = props => <CustomTypography variant="title" level={5} {...props} />;
Heading5.displayName = "CustomTypography.Heading5";
CustomTypography.Heading5 = Heading5;

// TEXT SIZES
const Body = props => <CustomTypography variant="text" {...props} />; // ⭐ TEXT BASE/NORMAL
Body.displayName = "CustomTypography.Body";
CustomTypography.Body = Body;

const BodyLarge = props => (
  <CustomTypography variant="text" style={{ fontSize: "16px" }} {...props} />
);
BodyLarge.displayName = "CustomTypography.BodyLarge";
CustomTypography.BodyLarge = BodyLarge;

const BodySmall = props => (
  <CustomTypography variant="text" style={{ fontSize: "12px" }} {...props} />
);
BodySmall.displayName = "CustomTypography.BodySmall";
CustomTypography.BodySmall = BodySmall;

const Caption = props => (
  <CustomTypography variant="text" type="secondary" style={{ fontSize: "12px" }} {...props} />
);
Caption.displayName = "CustomTypography.Caption";
CustomTypography.Caption = Caption;

// TEXT COLORS
const Success = props => <CustomTypography variant="text" type="success" {...props} />;
Success.displayName = "CustomTypography.Success";
CustomTypography.Success = Success;

const Warning = props => <CustomTypography variant="text" type="warning" {...props} />;
Warning.displayName = "CustomTypography.Warning";
CustomTypography.Warning = Warning;

const Danger = props => <CustomTypography variant="text" type="danger" {...props} />;
Danger.displayName = "CustomTypography.Danger";
CustomTypography.Danger = Danger;

const Secondary = props => <CustomTypography variant="text" type="secondary" {...props} />;
Secondary.displayName = "CustomTypography.Secondary";
CustomTypography.Secondary = Secondary;

const LinkComponent = props => <CustomTypography variant="link" {...props} />;
LinkComponent.displayName = "CustomTypography.Link";
CustomTypography.Link = LinkComponent;

// SPECIAL TYPES
const CustomLink = props => <CustomTypography variant="link" {...props} />;
CustomLink.displayName = "CustomTypography.Link";
CustomTypography.Link = CustomLink;

const Code = props => <CustomTypography variant="text" code {...props} />;
Code.displayName = "CustomTypography.Code";
CustomTypography.Code = Code;

const Strong = props => <CustomTypography variant="text" strong {...props} />;
Strong.displayName = "CustomTypography.Strong";
CustomTypography.Strong = Strong;

const Italic = props => <CustomTypography variant="text" italic {...props} />;
Italic.displayName = "CustomTypography.Italic";
CustomTypography.Italic = Italic;

const Mark = props => <CustomTypography variant="text" mark {...props} />;
Mark.displayName = "CustomTypography.Mark";
CustomTypography.Mark = Mark;

export default CustomTypography;
